"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.variableValPerf = exports.variableDepPerf = exports.VariableReference = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _reactivity = require("@vue/reactivity");
var _justPerformance = require("just-performance");
var _eventer = require("@blockwell/eventer");
var _ErrorValueTypeHandler = require("../type/ErrorValueTypeHandler");
let varIndex = 1;
const variableDepPerf = exports.variableDepPerf = {};
const variableValPerf = exports.variableValPerf = {};

/**
 * A reactive variable.
 */
class VariableReference {
  constructor(type, param, builtIn) {
    /**
     * A deferred computed value that collects all the variable's dependencies.
     */
    (0, _defineProperty2.default)(this, "deps", void 0);
    (0, _defineProperty2.default)(this, "depsRef", void 0);
    /**
     * Variable indexes this variable depends on.
     */
    (0, _defineProperty2.default)(this, "refDeps", void 0);
    /**
     * Reactive reference to the variable's value.
     */
    (0, _defineProperty2.default)(this, "ref", void 0);
    /**
     * The main reactive effect for the variable's value.
     */
    (0, _defineProperty2.default)(this, "effect", void 0);
    /**
     * Reactive effect for emitting events when the value changes.
     */
    (0, _defineProperty2.default)(this, "eventEffect", void 0);
    /**
     * Batcher for reading external data.
     */
    (0, _defineProperty2.default)(this, "batcher", void 0);
    (0, _defineProperty2.default)(this, "_param", void 0);
    (0, _defineProperty2.default)(this, "retPromise", void 0);
    (0, _defineProperty2.default)(this, "effected", false);
    (0, _defineProperty2.default)(this, "resolver", void 0);
    (0, _defineProperty2.default)(this, "index", void 0);
    (0, _defineProperty2.default)(this, "events", new _eventer.Eventer());
    this.type = type;
    this.builtIn = builtIn;
    this.index = varIndex++;
    this._param = param;
  }
  get param() {
    return this._param;
  }
  get valueType() {
    return this.type.valueType;
  }
  get variableType() {
    return this.type.type;
  }
  attach(resolver, batcher) {
    this.resolver = resolver;
    this.batcher = batcher;
  }
  value() {
    if (!this.resolver) {
      return {
        type: "error",
        value: new _ErrorValueTypeHandler.ValueTypeError("no_resolver", "Variable has no resolver, can't get value")
      };
    }

    // Only create a reactive effect if one doesn't already exist
    if (!this.effect) {
      this.ref = (0, _reactivity.ref)();
      this.depsRef = (0, _reactivity.ref)(1);
      let param = this._param;
      this.eventEffect = (0, _reactivity.effect)(() => {
        var _this$ref;
        this.events.emit("value", {
          value: (_this$ref = this.ref) === null || _this$ref === void 0 ? void 0 : _this$ref.value,
          type: this.valueType
        });
      }).effect;
      this.deps = (0, _reactivity.deferredComputed)(() => {
        // The return value from deps will contain both the dependencies and a potential error,
        // so that the effect can deal with the error.
        let depsResult = {
          deps: null,
          depsError: null
        };

        // Track dependencies for this variable
        this.resolver.trackLayer(this.index);
        if (!variableDepPerf[this.type.type]) {
          variableDepPerf[this.type.type] = [];
        }
        let start = _justPerformance.performance.now();
        try {
          let depsIndex = this.depsRef.value;
          depsResult.deps = this.type.dependencies(param, this.resolver);
          variableDepPerf[this.type.type].push(_justPerformance.performance.now() - start);
        } catch (err) {
          depsResult.depsError = new _ErrorValueTypeHandler.ValueTypeError("dependency_error", err.message);
          console.error(err);
        }
        this.refDeps = this.resolver.endTrack();
        this.events.emit("deps", depsResult.deps);
        return depsResult;
      });
      this.effect = (0, _reactivity.effect)(() => {
        this.effected = true;
        let {
          deps,
          depsError
        } = this.deps.value;
        if (depsError) {
          // If there was an error in dependencies, use that as the value
          this.ref.value = depsError;
          return;
        }
        let ref = this.ref;
        if (!ref) {
          console.warn("No ref in effect function, probably run after stopping");
          return;
        }

        // Remove a previous listener if it exists
        this.batcher.variableUpdates.removeListener(this.index);
        this.batcher.variableUpdates.activeVariable = this.index;

        // getValue will assign the variable's true value to the ref passed here, and
        // any return data is going to be an error.
        let ret = this.type.getValue(ref, deps, this.batcher);
        let retPromise = Promise.resolve(ret);
        this.retPromise = retPromise;
        retPromise.then(err => {
          if (err) {
            // Make sure the effect wasn't rerun while the return promise was resolving
            if (this.retPromise === retPromise) {
              ref.value = err;
            }
          }
        });
      }).effect;
    }
    let value = this.ref.value;
    return {
      type: value instanceof _ErrorValueTypeHandler.ValueTypeError ? "error" : this.valueType,
      value,
      dependencies: this.refDeps
    };
  }
  stop() {
    var _this$effect, _this$deps, _this$deps$effect, _this$eventEffect, _this$batcher, _this$batcher$variabl;
    (_this$effect = this.effect) === null || _this$effect === void 0 ? void 0 : _this$effect.stop();
    (_this$deps = this.deps) === null || _this$deps === void 0 ? void 0 : (_this$deps$effect = _this$deps.effect) === null || _this$deps$effect === void 0 ? void 0 : _this$deps$effect.stop();
    (_this$eventEffect = this.eventEffect) === null || _this$eventEffect === void 0 ? void 0 : _this$eventEffect.stop();
    this.deps = undefined;
    this.ref = undefined;
    this.effect = undefined;
    this.eventEffect = undefined;
    this.resolver = undefined;
    this.events.clearListeners();
    (_this$batcher = this.batcher) === null || _this$batcher === void 0 ? void 0 : (_this$batcher$variabl = _this$batcher.variableUpdates) === null || _this$batcher$variabl === void 0 ? void 0 : _this$batcher$variabl.removeListener(this.index);
    if (this.type.stop) {
      this.type.stop();
    }
  }
  trigger() {
    if (this.ref) {
      (0, _reactivity.triggerRef)(this.ref);
    }
  }
  triggerDeps() {
    if (this.depsRef) {
      this.depsRef.value += 1;
    }
  }
  triggerIfNeeded() {
    if (!this.effected) {
      this.trigger();
    }
  }
  updateParameter(key, value) {
    this._param[key] = value;
  }
  updateAllParameters(value) {
    let newKeys = Object.keys(value);
    let old = new Set(Object.keys(this._param));
    for (let it of newKeys) {
      // @ts-ignore
      this._param[it] = value[it];
      old.delete(it);
    }
    for (let key of old) {
      // @ts-ignore
      delete this._param[key];
    }
  }
  onValue(listener) {
    this.events.on("value", listener);
  }
  offValue(listener) {
    this.events.off("value", listener);
  }
  onDeps(listener) {
    this.events.on("deps", listener);
  }
  offDeps(listener) {
    this.events.off("deps", listener);
  }
  dependsOn(index) {
    var _this$refDeps2;
    if (Array.isArray(index)) {
      var _this$refDeps;
      return index.includes(this.index) || !!((_this$refDeps = this.refDeps) !== null && _this$refDeps !== void 0 && _this$refDeps.find(it => index.includes(it)));
    }
    return this.index === index || ((_this$refDeps2 = this.refDeps) === null || _this$refDeps2 === void 0 ? void 0 : _this$refDeps2.includes(index));
  }
}
exports.VariableReference = VariableReference;
//# sourceMappingURL=VariableReference.js.map